import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IPageHeaderVersionState, PAGE_HEADER_VERSION_STATE } from './page-header.config';

@Injectable({
    providedIn: 'root'
})
export class PageHeaderService implements OnDestroy {
    state$ = new BehaviorSubject<IPageHeaderVersionState | undefined>(undefined);
    highPriorityState$ = new BehaviorSubject<IPageHeaderVersionState | undefined>(undefined);
    lowPriorityState$ = new BehaviorSubject<IPageHeaderVersionState | undefined>(undefined);
    resetState$ = new Subject<void>();
    destroyed = new Subject<void>();

    constructor() {
        merge(this.lowPriorityState$, this.highPriorityState$)
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => this.setState());

        this.resetState$.subscribe(() => this.resetState());
    }

    setState(): void {
        if (this.highPriorityState$.value) {
            this.state$.next(this.highPriorityState$.value);
        } else if (this.lowPriorityState$.value) {
            this.state$.next(this.lowPriorityState$.value);
        } else {
            this.state$.next(PAGE_HEADER_VERSION_STATE);
        }
    }

    getState(): Observable<IPageHeaderVersionState | undefined> {
        return this.state$.pipe(
            distinctUntilChanged(
                (prev, curr) =>
                    prev?.transparent === curr?.transparent && prev?.lightMode === curr?.lightMode && prev?.hideUp === curr?.hideUp
            )
        );
    }

    setLowPriorityState(state: IPageHeaderVersionState | undefined) {
        if (state && (state.transparent !== undefined || state.lightMode !== undefined || state.hideUp !== undefined)) {
            this.lowPriorityState$.next(state);
        }
    }

    resetLowPriorityState() {
        this.lowPriorityState$.next(undefined);
    }

    setHighPriorityState(state: IPageHeaderVersionState | undefined) {
        if (state && (state.transparent !== undefined || state.lightMode !== undefined || state.hideUp !== undefined)) {
            this.highPriorityState$.next(state);
        }
    }

    resetHighPriorityState() {
        this.highPriorityState$.next(undefined);
    }

    resetState() {
        this.resetLowPriorityState();
        this.resetHighPriorityState();
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }
}
